import React, { useState, useEffect, useMemo } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";
import RightsCheck from "../General/RightsCheck";

const PrintIncomeExpenditure = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);
  const [LedgerIncome, setLedgerIncome] = useState([]);
  const [LedgerExpenses, setLedgerExpenses] = useState([]);
  const [IncomeTotal, setIncomeTotal] = useState(0);
  const [ExpanditureTotal, setExpanditureTotal] = useState(0);
  const [SurplusOrDeficit, setSurplusOrDeficit] = useState(0);

  const filters = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fyear =
      urlParams.get("fyear") ||
      localStorage.getItem("DefaultFYear") ||
      "";
    return {
      fyear,
      startDate:
        urlParams.get("startDate") ||
        urlParams.get("StartDate") ||
        (fyear ? `${fyear}-04-01` : ""),
      endDate: urlParams.get("endDate") || urlParams.get("EndDate") || "",
      accountLevel:
        urlParams.get("accountLevel") ||
        urlParams.get("Level") ||
        "0",
      showZeroAccounts:
        urlParams.get("showZeroAccounts") !== "0" &&
        urlParams.get("zeroAccount") !== "false",
      accountCode: (
        urlParams.get("accountCode") ||
        urlParams.get("AccountCode") ||
        ""
      ).trim(),
      accountName: (
        urlParams.get("accountName") ||
        urlParams.get("AccountName") ||
        ""
      ).trim(),
    };
  }, []);

  useEffect(() => {
    document.title = "Print Income and Expenditure";
    const year = parseInt(filters.fyear, 10);
    if (!year || !filters.endDate) {
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Accounts/GetIncomeExpenditure",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        pageNo: 1,
        pageSize: 10000,
        FYear: year,
        StartDate: filters.startDate,
        EndDate: filters.endDate,
        AccountLevel: parseInt(filters.accountLevel, 10) || 0,
        ShowZeroAccounts: filters.showZeroAccounts,
        AccountCode: filters.accountCode,
        AccountName: filters.accountName,
      },
    })
      .then((response) => {
        if (response.data?.status_code === 1) {
          setLedgerIncome(response.data?.IncomeAccounts ?? []);
          setLedgerExpenses(response.data?.ExpenditureAccounts ?? []);
          setIncomeTotal(response.data?.IncomeTotal ?? 0);
          setExpanditureTotal(response.data?.ExpanditureTotal ?? 0);
          setSurplusOrDeficit(response.data?.SurplusOrDeficit ?? 0);
        } else {
          setLedgerIncome([]);
          setLedgerExpenses([]);
        }
      })
      .catch(() => {
        setLedgerIncome([]);
        setLedgerExpenses([]);
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, filters]);

  const levelLabel =
    filters.accountLevel === "1"
      ? "1st Level"
      : filters.accountLevel === "2"
      ? "2nd Level"
      : filters.accountLevel === "3"
      ? "3rd Level"
      : "3rd Level (detail)";

  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) {
      return value ?? "";
    }
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderRows = (rows) => {
    if (loading) {
      return (
        <tr>
          <td colSpan={3} className="text-center">
            <Spinner animation="border" role="status" />
          </td>
        </tr>
      );
    }
    if (rows?.length > 0) {
      return rows.map((item, index) => (
        <tr key={index}>
          <td>{item.Account_Code}</td>
          <td>{item.Account_Description}</td>
          <td className="text-end">{formatAmount(item.Amount)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan={3} className="text-center text-muted">
          No Data Found
        </td>
      </tr>
    );
  };

  const surplusLabel = Number(SurplusOrDeficit) >= 0 ? "Surplus" : "Deficit";

  return (
    <>
      <RightsCheck />
      <Page orientation="landscape" size="a4">
        <table className="table table-sm table-bordered table-print">
          <thead>
            <tr>
              <th colSpan={6}>
                <PrintHeader title="Income and Expenditure Statement" />
              </th>
            </tr>
            <tr>
              <th colSpan={6}>
                Year: {filters.fyear} | Level: {levelLabel} | From:{" "}
                {filters.startDate || "—"} | To: {filters.endDate || "—"} |
                Zero rows: {filters.showZeroAccounts ? "Yes" : "No"}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={3} className="fw-bold">
                Income
              </td>
              <td colSpan={3} className="fw-bold">
                Expenditure
              </td>
            </tr>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th className="text-end">Amount</th>
              <th>Code</th>
              <th>Name</th>
              <th className="text-end">Amount</th>
            </tr>
            <tr>
              <td colSpan={3} style={{ verticalAlign: "top", width: "50%" }}>
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    {renderRows(LedgerIncome)}
                    <tr>
                      <td colSpan={2} className="text-end fw-bold">
                        Total
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(IncomeTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td colSpan={3} style={{ verticalAlign: "top", width: "50%" }}>
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    {renderRows(LedgerExpenses)}
                    <tr>
                      <td colSpan={2} className="text-end fw-bold">
                        Total
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(ExpanditureTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td colSpan={6} className="text-end fw-bold">
                {surplusLabel}: {formatAmount(Math.abs(SurplusOrDeficit))} (Income{" "}
                {formatAmount(IncomeTotal)} − Expenditure{" "}
                {formatAmount(ExpanditureTotal)})
              </td>
            </tr>
          </tbody>
        </table>
      </Page>
    </>
  );
};

export default PrintIncomeExpenditure;
